import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { TrendUp, Quotes, Target, Pulse, Broadcast } from "@phosphor-icons/react";

const PC = { gpt: "#10A37F", claude: "#D97757", gemini: "#8E24AA" };
const PL = { gpt: "GPT", claude: "Claude", gemini: "Gemini" };

function fmtDate(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric" });
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="mb-1 font-medium">{fmtDate(label)}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5" style={{ color: p.color }}>
            <span className="h-2 w-2 rounded-full" style={{ background: p.color }} /> {PL[p.dataKey]}
          </span>
          <span className="font-mono">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent }) {
  return (
    <Card className="border-border bg-card p-5 transition-all duration-200 hover:-translate-y-1 hover:border-muted-foreground/30">
      <div className="flex items-start justify-between">
        <p className="overline text-muted-foreground">{label}</p>
        <Icon size={18} className="text-muted-foreground" style={accent ? { color: accent } : {}} />
      </div>
      <p className="mt-4 font-heading text-4xl font-extrabold tracking-tighter leading-none">{value}</p>
      {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

export default function Dashboard() {
  const { data: summary, isLoading: sl } = useQuery({
    queryKey: ["summary"], queryFn: async () => (await api.get("/dashboard/summary")).data,
  });
  const { data: trends, isLoading: tl } = useQuery({
    queryKey: ["trends"], queryFn: async () => (await api.get("/dashboard/trends?days=30")).data,
  });
  const { data: citations } = useQuery({
    queryKey: ["citations-feed"], queryFn: async () => (await api.get("/citations?limit=12")).data,
  });

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-8 fade-in">
        <p className="overline text-action">Overview</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter">Citation Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Brand mentions across GPT, Claude and Gemini over the last 30 days.</p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {sl ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={`kpi-skeleton-${i}`} className="h-32 rounded-lg" />)
        ) : (
          <>
            <Kpi label="Total Mentions" value={summary?.total_mentions ?? 0} sub="Cumulative brand citations" icon={Quotes} accent="#007AFF" />
            <Kpi label="Citation Rate" value={`${summary?.citation_rate ?? 0}%`} sub={`Across ${summary?.total_runs ?? 0} probes`} icon={Target} />
            <Kpi label="Active Queries" value={summary?.active_queries ?? 0} sub={`${summary?.query_count ?? 0} total tracked`} icon={Pulse} />
            <Kpi label="Providers" value={3} sub="GPT · Claude · Gemini" icon={Broadcast} />
          </>
        )}
      </div>

      {/* Chart + feed */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-4">
        <Card className="border-border bg-card p-5 lg:col-span-3">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="overline text-muted-foreground">Citation Trend</p>
              <h2 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2">
                <TrendUp size={18} className="text-action" /> Mentions over time
              </h2>
            </div>
            <div className="flex gap-4">
              {Object.keys(PC).map((k) => (
                <span key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ background: PC[k] }} /> {PL[k]}
                </span>
              ))}
            </div>
          </div>
          {tl ? (
            <Skeleton className="h-72 w-full rounded-md" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trends || []} margin={{ left: -18, right: 8, top: 4 }}>
                <defs>
                  {Object.entries(PC).map(([k, c]) => (
                    <linearGradient key={k} id={`g-${k}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={c} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                {Object.entries(PC).map(([k, c]) => (
                  <Area key={k} type="monotone" dataKey={k} stroke={c} strokeWidth={2} fill={`url(#g-${k})`} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Recent mentions feed */}
        <Card className="border-border bg-card p-5 lg:col-span-1">
          <p className="overline text-muted-foreground mb-4">Recent Probes</p>
          <div className="space-y-3" data-testid="recent-feed">
            {(citations || []).slice(0, 10).map((c, i) => (
              <div
                key={c.id}
                className="fade-in border-b border-border/40 pb-3 last:border-0"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: PC[c.provider] }}>
                    <span className="h-2 w-2 rounded-full" style={{ background: PC[c.provider] }} /> {PL[c.provider]}
                  </span>
                  {c.cited ? (
                    <Badge className="bg-success/15 text-success border-0 text-[10px]">×{c.mention_count}</Badge>
                  ) : (
                    <Badge variant="outline" className="border-border text-[10px] text-muted-foreground">no mention</Badge>
                  )}
                </div>
                <p className="mt-1 truncate text-xs text-muted-foreground">{c.prompt}</p>
              </div>
            ))}
            {(!citations || citations.length === 0) && (
              <p className="text-sm text-muted-foreground">No probes yet. Run a query to collect data.</p>
            )}
          </div>
        </Card>
      </div>

      {/* Per-provider breakdown */}
      {summary && (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {Object.entries(summary.per_provider).map(([k, v]) => (
            <Card key={k} className="border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-heading font-bold" style={{ color: PC[k] }}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: PC[k] }} /> {v.label}
                </span>
                <span className="font-mono text-2xl font-bold">{v.citation_rate}%</span>
              </div>
              <div className="mt-4 flex justify-between text-xs text-muted-foreground">
                <span>{v.mentions} mentions</span>
                <span>{v.cited}/{v.runs} cited</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full" style={{ width: `${v.citation_rate}%`, background: PC[k] }} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
